import { Button } from "@/marketing/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/marketing/components/ui/dialog";
import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import type { CampaignTrackerCellValue } from "./campaignOperationsPatches";

interface TrackerHistoryDialogProps {
  campaign: InfluencerCampaign | null;
  onClose: () => void;
  onRestore: (campaign: InfluencerCampaign, field: string, value: CampaignTrackerCellValue) => void;
}

function describe(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function restorable(value: unknown): value is CampaignTrackerCellValue {
  return value === null || typeof value === "string" || typeof value === "boolean";
}

/** Newest-first change history for one campaign, with a one-click restore of any earlier value. */
export function TrackerHistoryDialog({ campaign, onClose, onRestore }: TrackerHistoryDialogProps) {
  const entries = [...(campaign?.operations?.history ?? [])].reverse();
  return (
    <Dialog open={campaign !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="tw-max-w-2xl">
        <DialogHeader>
          <DialogTitle>Change history · {campaign?.name}</DialogTitle>
        </DialogHeader>
        {entries.length === 0 ? (
          <p className="tw-text-sm tw-text-muted-foreground">No tracker changes recorded yet.</p>
        ) : (
          <ul className="tw-max-h-[60vh] tw-space-y-1 tw-overflow-auto tw-text-xs">
            {entries.map((entry, index) => (
              <li key={`${entry.at}-${entry.field}-${index}`} className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-border tw-py-1.5">
                <span className="tw-w-32 tw-shrink-0 tw-text-muted-foreground">{entry.at.slice(0, 16).replace("T", " ")}</span>
                <span className="tw-w-40 tw-shrink-0 tw-truncate">{entry.by ?? "unknown"}</span>
                <span className="tw-min-w-0 tw-flex-1">
                  <code className="tw-text-[11px]">{entry.field}</code>: {describe(entry.from)} → <strong>{describe(entry.to)}</strong>
                </span>
                {campaign && restorable(entry.from) && (
                  <Button size="sm" variant="ghost" onClick={() => onRestore(campaign, entry.field, entry.from as CampaignTrackerCellValue)}>
                    Restore
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
